import type {
  IDateIntervalInput,
  IDeliveryHierarchyParentInput,
  ILocationBase,
} from '~/client/graph/types.generated'
import type { IResourceRecord } from '~/client/src/shared/resources/definitions/types'

import type { ResourceResult } from './resourceResult'
import { resourceErr, resourceOk } from './resourceResult'

type MaterialResourceDto = Pick<
  ILocationBase,
  | 'id'
  | 'name'
  | 'code'
  | 'isDeleted'
  | 'stxResourceId'
  | 'templateId'
  | 'data'
  | 'color'
  | 'iconName'
  | 'permittedCompanies'
  | 'isBookable'
  | 'maxOverlappingDeliveries'
  | 'maxBookingDuration'
  | 'assignedSitemaps'
  | 'assignedGlobes'
  | 'operatingIntervals'
  | 'parent'
> & { readonly rollups?: unknown }

/** Normalized Material resource retained for rendering and lossless edits. */
export interface MaterialResource {
  readonly id: string
  readonly name: string
  /** Soft-deleted rows are retained so workflows can still name their picks. */
  readonly isDeleted: boolean
  readonly code: string | null
  readonly stxResourceId: string | null
  readonly templateId: string | null
  readonly data: Readonly<Record<string, unknown>>
  readonly rollups: Readonly<Record<string, unknown>>
  readonly color: string
  readonly iconName: string
  readonly permittedCompanies: readonly string[]
  readonly isBookable: boolean
  readonly maxOverlappingDeliveries: number | null
  readonly maxBookingDuration: number | null
  readonly assignedSitemaps: readonly (string | null)[]
  readonly assignedGlobes: readonly (string | null)[]
  readonly operatingIntervals: readonly IDateIntervalInput[]
  readonly parent: IDeliveryHierarchyParentInput | null
}

/** Material decoding failure produced when the transport value has no identity. */
export interface MissingMaterialResourceId {
  readonly _tag: 'MissingMaterialResourceId'
}

const recordOf = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? { ...value }
    : {}

const idsOf = (values: readonly unknown[] | null | undefined): string[] =>
  (values ?? [])
    .filter(value => value !== null && value !== undefined)
    .map(String)

const nullableIdsOf = (
  values: readonly (string | null)[] | null | undefined,
): Array<string | null> => [...(values ?? [])]

/** Normalizes the generated GraphQL Material DTO at the transport boundary. */
export function parseMaterialResource(
  location: MaterialResourceDto,
): ResourceResult<MaterialResource, MissingMaterialResourceId> {
  if (location.id === null || location.id === undefined) {
    return resourceErr({ _tag: 'MissingMaterialResourceId' })
  }

  return resourceOk({
    id: String(location.id),
    name: location.name,
    isDeleted: !!location.isDeleted,
    code: location.code ?? null,
    stxResourceId: location.stxResourceId ?? null,
    templateId: location.templateId ?? null,
    data: recordOf(location.data),
    rollups: 'rollups' in location ? recordOf(location.rollups) : {},
    color: location.color,
    iconName: location.iconName ?? 'material',
    permittedCompanies: idsOf(location.permittedCompanies),
    isBookable: location.isBookable,
    maxOverlappingDeliveries: location.maxOverlappingDeliveries ?? null,
    maxBookingDuration: location.maxBookingDuration ?? null,
    assignedSitemaps: nullableIdsOf(location.assignedSitemaps),
    assignedGlobes: nullableIdsOf(location.assignedGlobes),
    operatingIntervals: (location.operatingIntervals ?? []).flatMap(interval =>
      interval ? [{ ...interval }] : [],
    ),
    parent: location.parent
      ? {
          parentId:
            location.parent.parentId === null ||
            location.parent.parentId === undefined
              ? null
              : String(location.parent.parentId),
        }
      : null,
  })
}

/** Projects a Material resource into the shared Resource UI record shape. */
export function toMaterialResourceRecord(
  resource: MaterialResource,
): IResourceRecord {
  const rootFields = {
    id: resource.id,
    name: resource.name,
    code: resource.code,
    stxResourceId: resource.stxResourceId,
    templateId: resource.templateId,
    color: resource.color,
    iconName: resource.iconName,
    parent: resource.parent,
  }

  return {
    ...rootFields,
    isDeleted: resource.isDeleted,
    data: { ...resource.data, ...resource.rollups },
    rollups: { ...resource.rollups },
    root: {
      ...rootFields,
      permittedCompanies: [...resource.permittedCompanies],
      isBookable: resource.isBookable,
      showInMap: true,
      workflowRestrictions: null,
      operatingIntervals: resource.operatingIntervals.map(interval => ({
        ...interval,
      })),
    },
  }
}
